/**
 * ContextTip — floating tip in the top-right corner of the canvas.
 *
 * Shows three kinds of tips, in priority order:
 *  1. External tips via tipEmitter (ruler hover, drag events)
 *  2. Context tips derived from playback / tool / scene state
 *  3. Random idle tips that cycle every ~12 s when nothing else is active
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { usePlaybackStore } from '../../stores/playbackStore';
import { useSceneStore } from '../../stores/sceneStore';
import { useToolStore } from '../../stores/toolStore';
import { tipEmitter } from '../../lib/tipEmitter';

// ─── Tip content ────────────────────────────────────────────────────────────

type Tip = { text: string };

const RANDOM_TIPS: Tip[] = [
  { text: 'Press ? to see all keyboard shortcuts' },
  { text: 'Hold Cmd and drag anywhere to temporarily pan the canvas' },
  { text: 'Ctrl+Z / Ctrl+Y to undo and redo changes' },
  { text: 'Double-click an asset in the library to add it' },
  { text: 'Press F to fit the scene to the screen' },
  { text: 'Right-click the canvas for more options' },
  { text: 'Drag assets from the library directly onto the canvas' },
  { text: 'Use loop in / out markers for partial playback loops' },
  { text: 'Select a layer then use the Inspector to tweak transforms' },
  { text: 'Cmd+click to add layers to selection · Shift+click for a range' },
];

// ─── Component ──────────────────────────────────────────────────────────────

const CONTEXT_DURATION = 5000;  // ms a context tip stays visible
const RANDOM_DURATION  = 10000; // ms between random tips

export default function ContextTip() {
  const isPlaying      = usePlaybackStore((s) => s.isPlaying);
  const activeTool     = useToolStore((s) => s.activeTool);
  const selectedLayerId = useSceneStore((s) => s.selectedLayerId);

  const [text, setText]       = useState<string | null>(null);
  const [visible, setVisible] = useState(false);

  const hideTimer    = useRef<ReturnType<typeof setTimeout> | null>(null);
  const randomTimer  = useRef<ReturnType<typeof setTimeout> | null>(null);
  const randomIdx    = useRef(Math.floor(Math.random() * RANDOM_TIPS.length));
  const prevPlaying  = useRef(isPlaying);
  const prevTool     = useRef(activeTool);
  const prevLayer    = useRef(selectedLayerId);

  const show = useCallback((tip: string, duration = CONTEXT_DURATION) => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    setText(tip);
    setVisible(true);
    hideTimer.current = setTimeout(() => setVisible(false), duration);
  }, []);

  // ── External tips via tipEmitter ──────────────────────────────────────────
  useEffect(() => {
    tipEmitter.on((tip) => show(tip, CONTEXT_DURATION));
    return () => tipEmitter.off();
  }, [show]);

  // ── Context tips on state changes ─────────────────────────────────────────
  useEffect(() => {
    // isPlaying toggled
    if (prevPlaying.current !== isPlaying) {
      prevPlaying.current = isPlaying;
      if (!isPlaying) {
        show('Use ← → to step through frames · Shift+← → for bigger jumps');
      } else {
        show('Press Space to pause · J / K / L to change playback speed');
      }
    }
  }, [isPlaying, show]);

  useEffect(() => {
    // Active tool changed
    if (prevTool.current === activeTool) return;
    prevTool.current = activeTool;
    const tips: Partial<Record<typeof activeTool, string>> = {
      pan:      'Drag to pan the canvas · Scroll wheel to zoom',
      rotate:   'Drag to rotate · Hold Shift to snap to 15° increments',
      scale:    'Drag handles to scale · Hold Shift for uniform scale',
      annotate: 'Draw freehand annotations · Pick a color in the toolbar',
      select:   'Click to select · Cmd+click to add/remove · Shift+click for range',
      move:     'Drag to reposition · Arrow keys for precise nudging',
    };
    const tip = tips[activeTool];
    if (tip) show(tip);
  }, [activeTool, show]);

  useEffect(() => {
    // Layer selection changed
    if (prevLayer.current === selectedLayerId) return;
    const wasNull = prevLayer.current === null;
    prevLayer.current = selectedLayerId;
    if (selectedLayerId && wasNull) {
      show('Press Space to play · ← → to step frames · I / Shift+I to mark loop in/out');
    }
  }, [selectedLayerId, show]);

  // ── Random idle tips ──────────────────────────────────────────────────────
  useEffect(() => {
    function scheduleNext() {
      randomTimer.current = setTimeout(() => {
        if (!visible) {
          randomIdx.current = (randomIdx.current + 1) % RANDOM_TIPS.length;
          show(RANDOM_TIPS[randomIdx.current].text, CONTEXT_DURATION);
        }
        scheduleNext();
      }, RANDOM_DURATION);
    }
    scheduleNext();
    return () => { if (randomTimer.current) clearTimeout(randomTimer.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!text) return null;

  return (
    <div
      className="absolute top-8 right-3 z-30 pointer-events-none select-none"
      style={{
        transition: 'opacity 0.35s ease, transform 0.35s ease',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(-4px)',
      }}
      onTransitionEnd={() => { if (!visible) setText(null); }}
    >
      <div className="flex items-center gap-1.5 bg-bg-elevated/90 backdrop-blur-sm border border-accent/30 rounded-md px-2.5 py-1.5 shadow-md max-w-[320px]">
        <span className="font-mono text-[9px] font-bold text-accent shrink-0 uppercase tracking-wider">Tip</span>
        <span className="font-sans text-[10px] text-text-secondary leading-tight">{text}</span>
      </div>
    </div>
  );
}
